import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { parseApiError } from '../../lib/apiErrors';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

const ForgotPasswordPage = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      // For testing, show the reset link
      if (response.data.reset_link) {
        setResetLink(response.data.reset_link);
      }
      toast({ 
        title: 'Email envoyé', 
        description: 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.' 
      });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: parseApiError(error),
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="The English Speaking Center" 
              className="h-16 mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-2xl font-bold text-[#2C3C65]">Mot de passe oublié</h1>
            <p className="text-[#6777A1] mt-2">
              Entrez votre email pour recevoir un lien de réinitialisation
            </p>
          </div>

          {submitted ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#2C3C65]">Email envoyé !</h2>
              <p className="text-[#6777A1]">
                Si un compte existe avec l'adresse {email}, vous recevrez un lien de réinitialisation.
              </p>
              
              {/* For testing - show reset link */}
              {resetLink && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700 mb-2">Lien de test (à supprimer en production) :</p>
                  <Link 
                    to={resetLink} 
                    className="text-[#285BBB] hover:underline text-sm break-all"
                  >
                    {window.location.origin}{resetLink}
                  </Link>
                </div>
              )}

              <Link 
                to="/login" 
                className="inline-flex items-center text-[#285BBB] hover:underline mt-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    className="w-full pl-10"
                    data-testid="forgot-password-email"
                  />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-[#6777A1] hover:text-[#285BBB]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
